import moment from 'moment-timezone';
import { useCallback, useEffect, useState } from 'react';

import { AIDENTIFIED_MATCHED_BY, DATE_TIME_FORMAT } from '../../../constants';
import {
  getAidentifiedUploadReport,
  getContactAidentifiedData,
} from '../../../services/aidentifiedDataApi';
import { UploadResult } from '../../AidentifiedData/UploadResult';
import { BasicModal, Button, Loader, ModalHeader } from '../../Shared';
import { AidentifiedFieldsModal } from '../AidentifiedFieldsModal';

const getMatchedByLabel = value =>
  AIDENTIFIED_MATCHED_BY.find(item => item.value === value)?.label ?? value;

const CELL_CLASS_NAME = 'py-2 px-3 text-left text-[14px]/[20px] text-base-1000';

export const AidentifiedReportModal = ({ upload, onClose }) => {
  const { importId, tenantId, fileName, tenantName, uploadedOn } = upload;

  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [viewedContact, setViewedContact] = useState(null);

  const fetchReport = useCallback(async () => {
    setIsLoading(true);
    setIsError(false);

    try {
      setReport(await getAidentifiedUploadReport(importId));
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [importId]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const loadContactData = useCallback(
    () => getContactAidentifiedData(tenantId, viewedContact.contactId),
    [tenantId, viewedContact],
  );

  return (
    <BasicModal open onClose={onClose}>
      <div className="bg-white w-[760px] max-w-full rounded-2xl shadow-[-40px_40px_80px_-8px_rgba(0,0,0,0.24)]">
        <ModalHeader
          title="Upload report"
          subtitle={`${fileName} · ${tenantName} · uploaded ${moment(uploadedOn).format(DATE_TIME_FORMAT.MM_DD_YYYY_hh_mm_A)}`}
          onClose={onClose}
        />
        <div className="flex flex-col gap-4 px-5 pb-5">
          {isLoading && (
            <div className="h-[240px] flex justify-center items-center">
              <Loader />
            </div>
          )}
          {isError && (
            <div className="h-[240px] flex flex-col justify-center items-center gap-3">
              <span className="text-base-800 text-[16px]/[24px]">
                Failed to load the report
              </span>
              <Button
                type="button"
                title="Reload"
                variant="blue"
                onClick={fetchReport}
              />
            </div>
          )}
          {!isLoading && !isError && report && (
            <>
              <UploadResult upload={report} />
              {report.contacts.length > 0 && (
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-base-1000 font-medium text-[16px]/[24px]">
                      Matched leads
                    </span>
                    <span className="text-base-800 text-[14px]/[20px]">
                      first {report.contacts.length} of{' '}
                      {report.rowsMatched.toLocaleString('en-US')}
                    </span>
                  </div>
                  <div className="max-h-[240px] overflow-auto rounded-xl border border-base-200">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-base-200">
                          <th className={`${CELL_CLASS_NAME} font-medium`}>
                            Contact Name
                          </th>
                          <th className={`${CELL_CLASS_NAME} font-medium`}>
                            Contact Company
                          </th>
                          <th className={`${CELL_CLASS_NAME} font-medium`}>
                            Matched by
                          </th>
                          <th className={CELL_CLASS_NAME} />
                        </tr>
                      </thead>
                      <tbody>
                        {report.contacts.map(contact => (
                          <tr
                            key={contact.contactId}
                            className="border-b border-base-200 last:border-none"
                          >
                            <td className={CELL_CLASS_NAME}>
                              {contact.contactName || '-'}
                            </td>
                            <td className={CELL_CLASS_NAME}>
                              {contact.companyName || '-'}
                            </td>
                            <td className={CELL_CLASS_NAME}>
                              {getMatchedByLabel(contact.matchedBy)}
                            </td>
                            <td className={`${CELL_CLASS_NAME} text-right`}>
                              <button
                                type="button"
                                className="text-brand-900 underline"
                                onClick={() => setViewedContact(contact)}
                              >
                                View data
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {viewedContact && (
        <AidentifiedFieldsModal
          loadData={loadContactData}
          contactName={viewedContact.contactName}
          contactCompany={viewedContact.companyName}
          tenant={tenantName}
          onClose={() => setViewedContact(null)}
        />
      )}
    </BasicModal>
  );
};
